import { useMemo, useState } from 'react';
import type { CollectionBindingOverride, ComponentContentSchema } from '@vibesites/runtime';
import { resolveScopedOverride } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableToken,
  buildScopeContext,
  useCollectionBinding,
  useContentToken,
  useVibesites,
} from '@vibesites/runtime/react';
import {
  GalleryGrid,
  GalleryLightbox,
  GallerySharedStyles,
  inferPhotoBucket,
  sortGalleryImages,
  type GalleryImage,
} from './_galleryShared';

const IMAGES_BINDING = 'photogallery.images';

type GalleryTab = 'all' | 'community' | 'residences';

/** True when the editor saved a custom gallery order (id-mode or legacy index-mode). */
function hasManualGalleryOrder(override: CollectionBindingOverride | null | undefined): boolean {
  if (!override) return false;
  if (override.orderedIds != null) return true;
  return Boolean(override.ordering?.length);
}

/**
 * Unified photo gallery — all property photos in a 16:9 tile grid (2 columns
 * on tablet, 3 on desktop) with a swipeable single-image carousel on mobile.
 */
export function PhotoGallery() {
  const { collectionBindingOverrides, page } = useVibesites();
  const images = useCollectionBinding<GalleryImage>(IMAGES_BINDING, {
    collection: 'photo_gallery',
  });

  const bindingOverride = useMemo(
    () =>
      resolveScopedOverride(
        collectionBindingOverrides,
        IMAGES_BINDING,
        buildScopeContext(page),
      ) ?? null,
    [collectionBindingOverrides, page],
  );

  const photoList = useMemo(() => {
    const list = images && images.length > 0 ? images : [];
    const excludedTitles = new Set(['other', 'floorplan', 'logo']);
    const filtered = list.filter(
      (img) =>
        Boolean(img.image) &&
        !excludedTitles.has((img.title || '').toLowerCase().trim()),
    );
    // Manual editor reorder wins over keyword auto-sort.
    if (hasManualGalleryOrder(bindingOverride)) return filtered;
    return sortGalleryImages(filtered);
  }, [images, bindingOverride]);

  const tabAllLabel = useContentToken('photogallery.tab_all');
  const tabCommunityLabel = useContentToken('photogallery.tab_community');
  const tabResidencesLabel = useContentToken('photogallery.tab_residences');

  const tabPhotos = useMemo(() => {
    const community: GalleryImage[] = [];
    const residences: GalleryImage[] = [];
    for (const photo of photoList) {
      const bucket = inferPhotoBucket(photo);
      if (bucket === 'community') community.push(photo);
      else if (bucket === 'apartment') residences.push(photo);
    }
    return { all: photoList, community, residences };
  }, [photoList]);

  const tabs: { id: GalleryTab; label: string }[] = [
    { id: 'all', label: tabAllLabel || 'All' },
    { id: 'community', label: tabCommunityLabel || 'Community' },
    { id: 'residences', label: tabResidencesLabel || 'Residences' },
  ];
  const availableTabs = tabs.filter((tab) => tab.id === 'all' || tabPhotos[tab.id].length > 0);
  // With only one populated category, "All" would duplicate it — skip the tab bar.
  const showTabs = availableTabs.length > 2;

  const [activeTab, setActiveTab] = useState<GalleryTab>('all');
  const currentTab: GalleryTab =
    showTabs && availableTabs.some((tab) => tab.id === activeTab) ? activeTab : 'all';
  const visiblePhotos = tabPhotos[currentTab];

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const selectTab = (tab: GalleryTab) => {
    setActiveTab(tab);
    setSelectedIndex(null);
  };

  if (photoList.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained py-8 md:py-12">
        {showTabs && (
          <div className="photo-gallery-tabs" role="tablist" aria-label="Filter gallery photos">
            {availableTabs.map((tab) => {
              const isActive = tab.id === currentTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="photo-gallery-panel"
                  className={`photo-gallery-tab${isActive ? ' is-active' : ''}`}
                  onClick={() => selectTab(tab.id)}
                >
                  {tab.id === 'all' && (
                    <EditableToken path="photogallery.tab_all">{tab.label}</EditableToken>
                  )}
                  {tab.id === 'community' && (
                    <EditableToken path="photogallery.tab_community">{tab.label}</EditableToken>
                  )}
                  {tab.id === 'residences' && (
                    <EditableToken path="photogallery.tab_residences">{tab.label}</EditableToken>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <EditableCollectionBinding bindingPath={IMAGES_BINDING} label="Gallery Photos">
          <div
            key={currentTab}
            id="photo-gallery-panel"
            role={showTabs ? 'tabpanel' : undefined}
          >
            <GalleryGrid images={visiblePhotos} onSelect={setSelectedIndex} />
          </div>
        </EditableCollectionBinding>
      </div>

      <GalleryLightbox
        images={visiblePhotos}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />
      <PhotoGalleryTabStyles />
    </section>
  );
}

function PhotoGalleryTabStyles() {
  return (
    <style>{`
      .photo-gallery-tabs {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.25rem;
        margin-bottom: 1.5rem;
      }

      .photo-gallery-tab {
        font-family: var(--vibe-font-body);
        font-size: 0.8rem;
        font-weight: 500;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        padding: 0.5rem 1.25rem;
        border: 1px solid var(--vibe-color-border);
        background: transparent;
        color: var(--vibe-color-text-muted);
        cursor: pointer;
        transition: background 0.2s, color 0.2s, border-color 0.2s;
      }

      .photo-gallery-tab:first-child { border-radius: 3px 0 0 3px; }
      .photo-gallery-tab:last-child { border-radius: 0 3px 3px 0; }

      .photo-gallery-tab:hover {
        color: var(--vibe-color-text);
        border-color: var(--vibe-color-text-muted);
      }

      .photo-gallery-tab:focus-visible {
        outline: 2px solid var(--vibe-color-primary);
        outline-offset: 2px;
      }

      .photo-gallery-tab.is-active,
      .photo-gallery-tab.is-active:hover {
        background: var(--vibe-color-primary);
        border-color: var(--vibe-color-primary);
        color: var(--vibe-color-text-on-primary);
      }

      @media (max-width: 639px) {
        .photo-gallery-tabs {
          padding: 0 1rem;
        }
      }

      @media (max-width: 479px) {
        .photo-gallery-tab {
          padding: 0.5rem 0.875rem;
        }
      }
    `}</style>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PhotoGallery',
  displayName: 'Photo Gallery',
  tokens: {
    'photogallery.tab_all': {
      label: 'All Tab Label',
      type: 'text',
      defaultValue: 'All',
    },
    'photogallery.tab_community': {
      label: 'Community Tab Label',
      type: 'text',
      defaultValue: 'Community',
    },
    'photogallery.tab_residences': {
      label: 'Residences Tab Label',
      type: 'text',
      defaultValue: 'Residences',
    },
  },
  collectionBindings: {
    'photogallery.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description: 'All photos displayed in the gallery grid.',
    },
  },
};

(PhotoGallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PhotoGallery;
